import React from 'react'

// Utils
import { dateFormat } from '@sketch/utils'
import { PLAN_TYPE_LABEL } from 'modules/workspace/utils'

// Components
import { Pluralize } from '@sketch/components'
import BillingInfoTable from '../BillingInfoTable'

// Styles
import { ErrorPill, Description, StyledButton } from './BillingCycle.styles'

// Types
import {
  BillingSeatsInfoFragment,
  BillingPlanFragment,
} from '@sketch/gql-types'

interface BillingCycleProps {
  subscriptionEnd?: string
  nextBillingCycleDate?: string
  seats: BillingSeatsInfoFragment
  isWorkspaceOnTrial: boolean
  customerHasPaymentDetails: boolean
  currentPlan?: BillingPlanFragment

  onCancelSeatScheduleChange: () => void
}

// Helpers
const getHeaderCopy = (isWorkspaceOnTrial: boolean, isEducationPlan: boolean) =>
  isWorkspaceOnTrial || isEducationPlan
    ? 'First Billing Date'
    : 'Next Billing Date'

/**
 * BillingCycle
 *
 * Rends the "Next Billing Cycle" panel in the Plan Overview Panel (Workspace Settings)
 */
const BillingCycle: React.FC<BillingCycleProps> = props => {
  const {
    subscriptionEnd,
    nextBillingCycleDate,
    seats,
    isWorkspaceOnTrial,
    onCancelSeatScheduleChange,
    customerHasPaymentDetails,
    currentPlan,
  } = props

  const isScheduled = !!seats.scheduledSeatsTotal
  const isEducationPlan = currentPlan?.product === 'STUDENT'

  if (subscriptionEnd) {
    return (
      <BillingInfoTable.Row>
        <BillingInfoTable.HeadingCell>
          Next Billing Date
        </BillingInfoTable.HeadingCell>
        <BillingInfoTable.Cell data-testid="plan-next-cycle">
          <div>
            <ErrorPill data-testid="warning-subscription-badge">
              Subscription Canceled
            </ErrorPill>
            <br />
            <span>
              You can keep using the Workspace until{' '}
              {dateFormat(new Date(subscriptionEnd))}
            </span>
          </div>
        </BillingInfoTable.Cell>
      </BillingInfoTable.Row>
    )
  }

  return (
    <BillingInfoTable.Row>
      <BillingInfoTable.HeadingCell>
        {getHeaderCopy(isWorkspaceOnTrial, isEducationPlan)}
      </BillingInfoTable.HeadingCell>
      <BillingInfoTable.Cell data-testid="plan-next-cycle">
        <Description>
          <strong>{dateFormat(new Date(nextBillingCycleDate!))}</strong>
          <br />
          {isWorkspaceOnTrial && !customerHasPaymentDetails && (
            <>If you subscribe, you’ll be billed on this date</>
          )}
          {isWorkspaceOnTrial && customerHasPaymentDetails && (
            <>
              You&apos;ll be billed{' '}
              {PLAN_TYPE_LABEL[currentPlan!.type].toLowerCase()} for Editor
              Seats you&apos;re using at the end of your trial.
              <br />
              <br />
            </>
          )}
          {!isWorkspaceOnTrial && isScheduled && !isEducationPlan && (
            <>
              You have scheduled a change to have {seats.scheduledSeatsTotal}{' '}
              Editor seats on the date above.{' '}
              <StyledButton onClick={onCancelSeatScheduleChange}>
                Cancel scheduled change?
              </StyledButton>
              <br />
              <br />
            </>
          )}
          {isEducationPlan && customerHasPaymentDetails && (
            <>
              You’ll be billed for{' '}
              {seats.scheduledSeatsTotal ?? seats.currentSeatsTotal} Editor{' '}
              <Pluralize
                singular="Seat"
                plural="Seats"
                count={seats.scheduledSeatsTotal ?? seats.currentSeatsTotal}
                wrapperElement={React.Fragment}
              />{' '}
              at the end of your Education plan.
              <br />
              <br />
            </>
          )}
        </Description>
      </BillingInfoTable.Cell>
    </BillingInfoTable.Row>
  )
}

export default BillingCycle
